import {
    CHUNK_SIZE,
    CHUNKS_LOADED,
    CosmologyZone,
    MENTOR_FACING,
    MENTOR_PLATFORM_X,
    MOVE_INTERVAL,
    PlayerState,
    RodentType,
    TILE_SIZE,
    TileType,
    calculateBaseSurfaceY,
    calculatePortalSurfaceY,
    getCreatureColor,
    getCreatureAI,
    getCreatureSize,
    getZoneAtTile,
    isKasiPlaceholder,
} from '@blockworld/shared';

export interface GameSceneRenderContext {
    [key: string]: any;
}

/**
 * Extracted from GameScene.render as a move-only refactor.
 */
export function renderSceneFrame(scene: GameSceneRenderContext, now: number): void {
    if (!scene.localPlayer) return;

    const lp = scene.localPlayer;

    // Camera offset
    const screenCenterX = scene.cameras.main.width / 2;
    const screenCenterY = scene.cameras.main.height / 2;
    const playerScreenX = screenCenterX - TILE_SIZE / 2;
    const playerScreenY = screenCenterY - TILE_SIZE; // Top of 2-tile player
    const offsetX = playerScreenX - lp.x * TILE_SIZE;
    const offsetY = playerScreenY - lp.y * TILE_SIZE;

    // Skybox parallax, constellation overlays, shooting star, twinkle, glimmer, room boxes
    // Include NPC speech bubbles so constellation overlays light up for their glyphs
    const mentorBubble = scene.mentorBehavior?.getActiveBubble?.() ?? null;
    const loverBubble = scene.loverBehavior?.getActiveBubble?.() ?? null;
    if (mentorBubble) scene.otherPlayerSpeechBubbles.set('__mentor__', mentorBubble);
    if (loverBubble) scene.otherPlayerSpeechBubbles.set('__lover__', loverBubble);
    scene.skybox.renderFrame(
        now, lp.x, lp.y, offsetX, offsetY,
        scene.roomBoxes,
        scene.localSpeechBubble,
        scene.otherPlayerSpeechBubbles,
    );
    if (mentorBubble) scene.otherPlayerSpeechBubbles.delete('__mentor__');
    if (loverBubble) scene.otherPlayerSpeechBubbles.delete('__lover__');

    // Tiles - clear previous frame's active tiles
    const previousTileKeys = scene.activeTileKeys;
    scene.activeTileKeys = new Set();

    const halfChunks = Math.floor(CHUNKS_LOADED / 2);
    const centerCx = Math.floor(lp.x / CHUNK_SIZE);
    const centerCy = Math.floor(lp.y / CHUNK_SIZE);

    for (let cy = centerCy - halfChunks; cy <= centerCy + halfChunks; cy++) {
        for (let cx = centerCx - halfChunks; cx <= centerCx + halfChunks; cx++) {
            const chunk = scene.chunks.get(`${cx},${cy}`);
            if (!chunk) continue;

            for (let ly = 0; ly < CHUNK_SIZE; ly++) {
                for (let lx = 0; lx < CHUNK_SIZE; lx++) {
                    const worldX = cx * CHUNK_SIZE + lx;
                    const worldY = cy * CHUNK_SIZE + ly;
                    const tile = chunk[ly]?.[lx] ?? TileType.SKY;

                    // Determine if this tile is in a zone that needs black background
                    const zone = getZoneAtTile(worldX, worldY);
                    const isYggdrasilOrDungeon = zone === CosmologyZone.YGGDRASIL_TRUNK
                        || zone === CosmologyZone.YGGDRASIL_LEAVES
                        || zone === CosmologyZone.DUNGEON_TOWER;
                    const stoneLevel = calculateBaseSurfaceY(worldX, scene.worldSeed) + 4;
                    const isUnderground = isYggdrasilOrDungeon || worldY >= stoneLevel;

                    // SKY tiles: render as opaque black if underground,
                    // otherwise skip (background color/skybox shows through).
                    if (tile === TileType.SKY) {
                        if (!isUnderground) continue;
                    }

                    const sx = worldX * TILE_SIZE + offsetX;
                    const sy = worldY * TILE_SIZE + offsetY;

                    // Underground: render a black backdrop behind ALL non-SKY tiles
                    // so transparent gaps in ore/stone textures show black, not sky blue.
                    if (isUnderground && tile !== TileType.SKY) {
                        const bgKey = `bg_${worldX},${worldY}`;
                        scene.activeTileKeys.add(bgKey);
                        let bgSprite = scene.tileSprites.get(bgKey);
                        if (!bgSprite) {
                            bgSprite = scene.add.image(sx, sy, 'underground_generated');
                            bgSprite.setOrigin(0, 0);
                            bgSprite.setDepth(0);
                            scene.tileSprites.set(bgKey, bgSprite);
                        } else {
                            bgSprite.setPosition(sx, sy);
                            bgSprite.setDepth(0);
                            bgSprite.setVisible(true);
                        }
                    }

                    // Get texture name for tile type
                    let textureName: string;
                    if (tile === TileType.SKY) {
                        textureName = 'underground_generated';
                    } else if (tile === TileType.YELLOW_DOOR || tile === TileType.YELLOW_DOOR_OPEN) {
                        // Door sprites are 8x16 sliced into top/bottom halves.
                        // The top tile has another door tile directly below it.
                        const belowTile = scene.getTile(worldX, worldY + 1);
                        const isTop = belowTile === TileType.YELLOW_DOOR || belowTile === TileType.YELLOW_DOOR_OPEN;
                        if (tile === TileType.YELLOW_DOOR) {
                            textureName = isTop
                                ? scene.processedTextures.yellow_door_top
                                : scene.processedTextures.yellow_door_bottom;
                        } else {
                            textureName = isTop
                                ? scene.processedTextures.yellow_door_open_top
                                : scene.processedTextures.yellow_door_open_bottom;
                        }
                    } else if (tile === TileType.ILO_ANU) {
                        // Dice: render face based on worldData
                        const face = scene.networkClient?.worldData?.getDiceFace?.(worldX, worldY) ?? 1;
                        textureName = `dice_face_${face}`;
                    } else if (tile === TileType.KIWEN_LA) {
                        // Pressure plate: white when active, default otherwise
                        const wd = scene.networkClient?.worldData;
                        if (wd?.pressurePlateActive?.has(`${worldX},${worldY}`)) {
                            textureName = scene.processedTextures.kiwen_la_active;
                        } else {
                            textureName = scene.getTileTexture(tile as TileType);
                        }
                    } else if (tile === TileType.KIWEN_NIMI) {
                        // Sign block: use glyph-specific texture
                        const wd = scene.networkClient?.worldData;
                        const glyphIdx = wd?.getSignGlyph?.(worldX, worldY) ?? 0;
                        textureName = `sign_glyph_${glyphIdx}`;
                        if (!scene.textures.exists(textureName)) {
                            textureName = scene.getTileTexture(tile as TileType);
                        }
                    } else if (tile === TileType.KIWEN_LUKIN) {
                        // Eye switch: active color when struck
                        const wd = scene.networkClient?.worldData;
                        const tick = Math.floor(Date.now() / MOVE_INTERVAL);
                        if (wd?.isEyeSwitchActive?.(worldX, worldY, tick)) {
                            textureName = scene.processedTextures.kiwen_lukin_active;
                        } else {
                            textureName = scene.getTileTexture(tile as TileType);
                        }
                    } else if (tile === TileType.KIWEN_SEME) {
                        // ? block: inert (darker) when used
                        const wd = scene.networkClient?.worldData;
                        if (wd?.isQuestionBlockInert?.(worldX, worldY)) {
                            textureName = scene.processedTextures.kiwen_seme_inert;
                        } else {
                            textureName = scene.getTileTexture(tile as TileType);
                        }
                    } else {
                        textureName = scene.getTileTexture(tile as TileType);
                    }
                    const tileKey = `${worldX},${worldY}`;

                    scene.activeTileKeys.add(tileKey);

                    // Underground sky at depth 1 (behind crouching players at 5)
                    // Portal tiles at depth 2 (cosmetic background)
                    // Passable KASI tiles at depth 110 (foreground foliage, in front of player at 100)
                    // All other tiles at depth 10
                    const isKasiFoliage = isKasiPlaceholder(tile as TileType)
                        || tile === TileType.KASI_IJO || tile === TileType.KASI_UTALA
                        || tile === TileType.KASI_LINJA;
                    const tileDepth = tile === TileType.SKY ? 1
                        : tile === TileType.PORTAL ? 2
                        : isKasiFoliage ? 110 : 10;

                    let tileSprite = scene.tileSprites.get(tileKey);
                    if (!tileSprite) {
                        tileSprite = scene.createImage(sx, sy, textureName);
                        tileSprite.setOrigin(0, 0);
                        tileSprite.setDepth(tileDepth);
                        scene.tileSprites.set(tileKey, tileSprite);
                    } else {
                        scene.applyTexture(tileSprite, textureName);
                        tileSprite.setPosition(sx, sy);
                        tileSprite.setDepth(tileDepth);
                        tileSprite.setVisible(true);
                    }
                }
            }
        }
    }

    // Hide tiles that are no longer visible
    for (const key of previousTileKeys) {
        if (!scene.activeTileKeys.has(key)) {
            const sprite = scene.tileSprites.get(key);
            if (sprite) {
                sprite.setVisible(false);
            }
        }
    }

    // Tick for hurt flash timing (used for both remote and local players below)
    const currentTick = Math.floor(Date.now() / MOVE_INTERVAL);

    // Other players (interpolated)
    scene.otherPlayers.forEach((queue: any, id: any) => {
        const snap = queue.getCurrentPosition();
        if (!snap) return;

        // Get player-specific hue-shifted textures
        const playerTextures = scene.textureManager.getPlayerTextures(id);

        let sprite = scene.otherPlayerSprites.get(id);
        if (!sprite) {
            sprite = scene.createSprite(0, 0, playerTextures.man_right);
            sprite.setOrigin(0, 0);
            sprite.setScale(1);
            sprite.setDepth(50);
            // No tint - using hue-shifted textures instead
            scene.otherPlayerSprites.set(id, sprite);
        }

        const sx = snap.x * TILE_SIZE + offsetX;
        const sy = snap.y * TILE_SIZE + offsetY;

        const remoteHurtUntil = scene.otherPlayerHurtUntilTick.get(id) || 0;
        const remoteIsHurt = currentTick < remoteHurtUntil;
        scene.updatePlayerSprite(sprite, snap.state, snap.facing, snap.isCrouching, playerTextures, remoteIsHurt, snap.hasBoots ?? false);

        // Crouching: draw 1 tile higher, depth behind tiles
        if (snap.isCrouching) {
            sprite.setPosition(sx, sy - TILE_SIZE);
            sprite.setDepth(5);
        } else {
            sprite.setPosition(sx, sy);
            sprite.setDepth(50);
        }
        sprite.setVisible(true);

        // Held block for other players
        let heldBlockSprite = scene.otherPlayerHeldBlocks.get(id);
        if (snap.isHoldingBlock && snap.heldBlockType >= 0) {
            const textureName = scene.getTileTexture(snap.heldBlockType as TileType);
            if (!heldBlockSprite) {
                heldBlockSprite = scene.createImage(0, 0, textureName);
                heldBlockSprite.setOrigin(0, 0);
                heldBlockSprite.setDepth(55); // Above other player sprites
                scene.otherPlayerHeldBlocks.set(id, heldBlockSprite);
            }
            // Use same sprite Y position as the player sprite
            const spriteY = snap.isCrouching ? sy - TILE_SIZE : sy;
            const blockY = spriteY - TILE_SIZE + 6; // Same offset as local player
            scene.applyTexture(heldBlockSprite, textureName);
            heldBlockSprite.setPosition(sx, blockY);
            heldBlockSprite.setVisible(true);
        } else if (heldBlockSprite) {
            heldBlockSprite.setVisible(false);
        }

        // Sword for other players
        scene.combatHUD.renderOtherPlayerSword(
            id, snap.isHoldingSword, snap.x, snap.y,
            snap.facing, snap.isCrouching, offsetX, offsetY,
        );
    });

    // Remove sprites for disconnected players
    for (const [id, sprite] of scene.otherPlayerSprites) {
        if (!scene.otherPlayers.has(id)) {
            sprite.destroy();
            scene.otherPlayerSprites.delete(id);
            // Also clean up held block sprite
            const heldBlockSprite = scene.otherPlayerHeldBlocks.get(id);
            if (heldBlockSprite) {
                heldBlockSprite.destroy();
                scene.otherPlayerHeldBlocks.delete(id);
            }
            // Clean up sword sprite
            scene.combatHUD.removePlayer(id);
            // Clean up player-specific textures
            scene.textureManager.removePlayerTextures(id);
            // Clean up hurt flash tracking
            scene.otherPlayerHurtUntilTick.delete(id);
        }
    }

    // Local player sprite
    const localIsHurt = currentTick < scene.hurtUntilTick;
    scene.updatePlayerSprite(scene.localPlayerSprite, lp.state, lp.facing, lp.isCrouching, undefined, localIsHurt, lp.hasBoots);

    // Crouching: draw 1 tile higher (sprite is still 2 tiles tall, but player.y shifted down)
    // Also set depth behind tiles when crouching
    if (lp.isCrouching) {
        scene.localPlayerSprite.setPosition(playerScreenX, playerScreenY - TILE_SIZE);
        scene.localPlayerSprite.setDepth(5); // Behind tiles
    } else {
        scene.localPlayerSprite.setPosition(playerScreenX, playerScreenY);
        scene.localPlayerSprite.setDepth(100); // In front of tiles
    }
    if (!scene.deathAnim.isActive) scene.localPlayerSprite.clearTint(); // No tint for local player

    // Portal "ni" indicator — show when standing on a rune
    if (scene.standingOnRune && scene.isOnGround(lp) && !scene.deathAnim.isActive) {
        if (!scene.portalIndicator) {
            scene.portalIndicator = scene.add.image(0, 0, 'toki_glyphs', 65); // 65 = "ni"
            scene.portalIndicator.setOrigin(0, 0);
            scene.portalIndicator.setTint(0xffffff);
            scene.portalIndicator.setDepth(150);
        }
        // 1 tile above player head
        const indicatorScreenX = playerScreenX;
        const indicatorScreenY = playerScreenY - TILE_SIZE;
        scene.portalIndicator.setPosition(indicatorScreenX, indicatorScreenY);
        scene.portalIndicator.setVisible(true);
    } else if (scene.portalIndicator) {
        scene.portalIndicator.setVisible(false);
    }

    // Compass (ILO_LON): draw a line from player toward origin (0,0)
    if (lp.equippedItem === TileType.ILO_LON) {
        if (!scene.compassLine) {
            scene.compassLine = scene.add.graphics();
            scene.compassLine.setDepth(150);
        }
        scene.compassLine.clear();
        const cx = playerScreenX + TILE_SIZE / 2;
        const cy = playerScreenY + TILE_SIZE / 2;
        const dx = 0 - lp.x; // direction to origin
        const dy = 0 - lp.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 0.01) {
            const nx = dx / dist;
            const ny = dy / dist;
            const lineLen = 12; // pixels
            scene.compassLine.lineStyle(1, 0xFF0000, 1);
            scene.compassLine.beginPath();
            scene.compassLine.moveTo(cx, cy);
            scene.compassLine.lineTo(cx + nx * lineLen, cy + ny * lineLen);
            scene.compassLine.strokePath();
        }
        scene.compassLine.setVisible(true);
    } else if (scene.compassLine) {
        scene.compassLine.setVisible(false);
    }

    // Held block
    if (lp.isHoldingBlock && lp.heldBlockType !== null) {
        // Use same sprite Y position as the player sprite
        const spriteY = lp.isCrouching ? playerScreenY - TILE_SIZE : playerScreenY;
        const blockY = spriteY - TILE_SIZE + 6; // 7/8 overlapping player sprite
        const textureName = scene.getTileTexture(lp.heldBlockType);
        scene.applyTexture(scene.localHeldBlockSprite, textureName);
        scene.localHeldBlockSprite.setPosition(playerScreenX, blockY);
        scene.localHeldBlockSprite.setVisible(true);
    } else {
        scene.localHeldBlockSprite.setVisible(false);
    }

    // Sword rendering
    scene.combatHUD.renderLocalSword(
        lp.isHoldingSword, lp.x, lp.y,
        lp.facing, lp.isCrouching, offsetX, offsetY,
        lp.equippedItem,
    );

    // Slash rendering - use slash.x/y which are synced to player movement in updateSlashes
    for (const [id, slash] of scene.activeSlashes) {
        // Use pre-calculated position from updateSlashes (synced to player movement)
        const currentX = slash.x;
        const currentY = slash.y;

        // Debug: detect when local slash is more than 2 tiles ahead of player
        if (slash.ownerId === scene.localPlayerId && lp) {
            const distance = Math.abs(currentX - lp.x);
            if (distance > 2) {
                console.log(`[SLASH TOO FAR] id=${id}, slashX=${currentX}, playerX=${lp.x}, distance=${distance}, facing=${slash.facing}, spawnX=${slash.spawnX}`);
            }
        }

        const slashScreenX = currentX * TILE_SIZE + offsetX;
        const slashScreenY = currentY * TILE_SIZE + offsetY;

        let slashSprite = scene.slashSprites.get(id);
        if (!slashSprite) {
            slashSprite = scene.createImage(slashScreenX, slashScreenY, scene.processedTextures.slash);
            slashSprite.setOrigin(0, 0);
            slashSprite.setDepth(80); // Above tiles, below UI
            scene.slashSprites.set(id, slashSprite);
        }
        slashSprite.setPosition(slashScreenX, slashScreenY);
        slashSprite.setFlipX(slash.facing === -1);  // Slash BMP faces RIGHT, flip for LEFT
        slashSprite.setVisible(true);
    }

    // Clean up slash sprites that are no longer active
    for (const [id, sprite] of scene.slashSprites) {
        if (!scene.activeSlashes.has(id)) {
            sprite.destroy();
            scene.slashSprites.delete(id);
        }
    }

    // Render mice (including multi-tile creatures)
    const aliveMice = scene.clientMouseSim.getMice();
    const aliveMouseIds = new Set(aliveMice.map((m: any) => m.id));

    for (const mouse of aliveMice) {
        const rodentType = mouse.rodentType || RodentType.MOUSE;
        const size = getCreatureSize(rodentType);
        const isMultiTile = size.width > 1 || size.height > 1;

        // Sprite pixel dimensions
        const spriteW = size.width * TILE_SIZE;
        const spriteH = size.height * TILE_SIZE;

        // Screen position: (x, y) = bottom-left of bounding box
        // Top-left of bounding box in world: (x, y - height + 1)
        const topLeftX = mouse.x * TILE_SIZE + offsetX;
        const topLeftY = (mouse.y - size.height + 1) * TILE_SIZE + offsetY;

        let mouseSprite = scene.mouseSprites.get(mouse.id);
        const textureKey = `rodent_${rodentType}`;
        if (!mouseSprite) {
            // Try atlas frame first (via rodentFrameMap), then standalone texture, then colored fallback
            const atlasFrame = scene.rodentFrameMap?.get(rodentType);
            if (atlasFrame && scene.atlasFrames?.has(atlasFrame)) {
                mouseSprite = scene.createImage(0, 0, atlasFrame);
            } else if (scene.textures.exists(textureKey)) {
                mouseSprite = scene.add.image(0, 0, textureKey);
            } else {
                // Generate colored fallback texture for creatures without a dedicated sprite
                const color = getCreatureColor(rodentType);
                const gfx = scene.make.graphics({}, false);
                gfx.fillStyle(color, 1);
                gfx.fillRect(0, 0, spriteW, spriteH);
                gfx.generateTexture(textureKey, spriteW, spriteH);
                gfx.destroy();
                mouseSprite = scene.add.image(0, 0, textureKey);
            }
            mouseSprite.setOrigin(0, 0);
            mouseSprite.setDepth(45); // Between tiles (10) and other players (50)
            scene.mouseSprites.set(mouse.id, mouseSprite);
        }
        // Position at top-left of bounding box
        mouseSprite.setPosition(topLeftX, topLeftY);
        mouseSprite.setDisplaySize(spriteW, spriteH);
        mouseSprite.setVisible(true);

        // Sprites are right-facing: flip when facing left
        if (mouse.facing === -1) {
            mouseSprite.setFlipX(true);
            // When flipped with origin(0,0), need to offset by width so it stays aligned
            mouseSprite.setPosition(topLeftX + spriteW, topLeftY);
        } else {
            mouseSprite.setFlipX(false);
            mouseSprite.setPosition(topLeftX, topLeftY);
        }

        // Squirrel rotation for wall-cling and ceiling-walk (1×1 only)
        const ai = getCreatureAI(rodentType);
        if (!isMultiTile && ai === 'cling') {
            // Reset to center-origin for rotation to work correctly
            mouseSprite.setOrigin(0.5, 0.5);
            mouseSprite.setPosition(
                topLeftX + TILE_SIZE / 2,
                topLeftY + TILE_SIZE / 2,
            );
            const wd = mouse.wallDir ?? 0;
            const vd = mouse.vertDir ?? 1;
            if (wd === -1) {
                if (vd === -1) {
                    mouseSprite.setFlipX(true);
                    mouseSprite.setAngle(90);
                } else {
                    mouseSprite.setFlipX(false);
                    mouseSprite.setAngle(90);
                }
            } else if (wd === 1) {
                if (vd === -1) {
                    mouseSprite.setFlipX(false);
                    mouseSprite.setAngle(-90);
                } else {
                    mouseSprite.setFlipX(true);
                    mouseSprite.setAngle(-90);
                }
            } else if (vd === -1) {
                mouseSprite.setFlipX(mouse.facing === 1);
                mouseSprite.setAngle(180);
            } else {
                mouseSprite.setFlipX(mouse.facing === -1);
                mouseSprite.setAngle(0);
            }
            mouseSprite.setFlipY(false);
        } else {
            mouseSprite.setFlipY(false);
            mouseSprite.setAngle(0);
        }
    }

    // Clean up sprites for dead mice
    for (const [id, sprite] of scene.mouseSprites) {
        if (!aliveMouseIds.has(id)) {
            sprite.destroy();
            scene.mouseSprites.delete(id);
        }
    }

    // Render NPC mentor (jan pu) on spawn platform
    if (scene.worldSeed !== 0) {
        const mentorWorldX = MENTOR_PLATFORM_X;
        // Mentor stands ON the platform: feet at surfaceY-1, head at surfaceY-2
        const surfaceY = calculatePortalSurfaceY(scene.worldSeed);
        const mentorWorldY = surfaceY - 2;
        const mentorScreenX = mentorWorldX * TILE_SIZE + offsetX;
        const mentorScreenY = mentorWorldY * TILE_SIZE + offsetY;

        if (!scene.mentorSprite) {
            scene.mentorSprite = scene.createImage(0, 0, scene.textureManager.processedTextures.mentor_right);
            scene.mentorSprite.setOrigin(0, 0);
            scene.mentorSprite.setDepth(48); // Between mice (45) and other players (50)
        }
        // Dynamic facing from MentorBehavior (turns toward entering players)
        const mentorFacing = scene.mentorBehavior?.facing ?? MENTOR_FACING;
        scene.mentorSprite.setFlipX(mentorFacing === -1);
        scene.mentorSprite.setPosition(mentorScreenX, mentorScreenY);
        scene.mentorSprite.setVisible(true);

        // Update mentor speech bubble position
        scene.mentorBehavior?.updateBubblePosition(offsetX, offsetY);
    }

    // Render NPC lover (jan olin) in exploration house
    if (scene.worldSeed !== 0 && scene.loverBehavior) {
        const loverWorldX = scene.loverBehavior.x;
        const loverWorldY = scene.loverBehavior.y;
        const loverScreenX = loverWorldX * TILE_SIZE + offsetX;
        const loverScreenY = loverWorldY * TILE_SIZE + offsetY;

        if (!scene.loverSprite) {
            scene.loverSprite = scene.createImage(0, 0, scene.textureManager.processedTextures.lover_right);
            scene.loverSprite.setOrigin(0, 0);
            scene.loverSprite.setDepth(48);
        }
        const loverFacing = scene.loverBehavior.facing;
        scene.loverSprite.setFlipX(loverFacing === -1);
        scene.loverSprite.setPosition(loverScreenX, loverScreenY);
        scene.loverSprite.setVisible(true);

        // Update lover speech bubble position
        scene.loverBehavior.updateBubblePosition(offsetX, offsetY);
    }

    // Render Hero NPC in underground cavern
    if (scene.heroBehavior && !scene.heroBehavior.destroyed) {
        const hero = scene.heroBehavior;
        const heroScreenX = hero.x * TILE_SIZE + offsetX;
        const heroScreenY = hero.y * TILE_SIZE + offsetY;

        if (!scene.heroSprite) {
            scene.heroSprite = scene.createImage(0, 0, scene.textureManager.processedTextures.hero_right);
            scene.heroSprite.setOrigin(0, 0);
            scene.heroSprite.setDepth(48);
        }

        const pt = scene.textureManager.processedTextures;
        const noboots = hero.droppedBoots;
        let texture = noboots ? pt.hero_noboots_right : pt.hero_right;
        if (hero.isCrouching || hero.state === PlayerState.JUMPSQUAT) {
            texture = noboots ? pt.hero_noboots_squat_right : pt.hero_squat_right;
        } else if (hero.state === PlayerState.RISING) {
            texture = noboots ? pt.hero_noboots_leap_right : pt.hero_leap_right;
        } else if (hero.state === PlayerState.FALLING) {
            texture = noboots ? pt.hero_noboots_fall_right : pt.hero_fall_right;
        }

        scene.applyTexture(scene.heroSprite, texture);
        scene.heroSprite.setFlipX(hero.facing === -1);
        scene.heroSprite.setPosition(heroScreenX, heroScreenY);
        scene.heroSprite.setVisible(true);

        // Held block on hero's head
        if (hero.isHoldingBlock && hero.heldBlockType !== null) {
            if (!scene.heroHeldBlockSprite) {
                scene.heroHeldBlockSprite = scene.createImage(0, 0, scene.getTileTexture(hero.heldBlockType));
                scene.heroHeldBlockSprite.setOrigin(0, 0);
                scene.heroHeldBlockSprite.setDepth(49);
            }
            const spriteY = hero.isCrouching ? heroScreenY - TILE_SIZE : heroScreenY;
            const blockY = spriteY - TILE_SIZE + 6;
            scene.applyTexture(scene.heroHeldBlockSprite, scene.getTileTexture(hero.heldBlockType));
            scene.heroHeldBlockSprite.setPosition(heroScreenX, blockY);
            scene.heroHeldBlockSprite.setVisible(true);
        } else if (scene.heroHeldBlockSprite) {
            scene.heroHeldBlockSprite.setVisible(false);
        }
    } else if (scene.heroBehavior?.destroyed) {
        // Hero escaped — hide sprites
        if (scene.heroSprite) scene.heroSprite.setVisible(false);
        if (scene.heroHeldBlockSprite) scene.heroHeldBlockSprite.setVisible(false);
    }

    // Update hero speech bubble position
    scene.heroBehavior?.updateBubblePosition(offsetX, offsetY);

    // Update speech bubble positions (using screen coordinates directly)
    if (scene.localSpeechBubble && scene.localSpeechBubble.isActive()) {
        scene.localSpeechBubble.updatePosition(playerScreenX, playerScreenY);
    } else if (scene.localSpeechBubble) {
        scene.localSpeechBubble = null;
    }

    // Update other player speech bubbles
    for (const [playerId, bubble] of scene.otherPlayerSpeechBubbles) {
        if (bubble.isActive()) {
            const queue = scene.otherPlayers.get(playerId);
            const pos = queue?.getCurrentPosition();
            if (pos) {
                const sx = pos.x * TILE_SIZE + offsetX;
                const sy = pos.y * TILE_SIZE + offsetY;
                bubble.updatePosition(sx, sy);
            }
        } else {
            scene.otherPlayerSpeechBubbles.delete(playerId);
        }
    }
}

/**
 * Show a hurt speech bubble (single "o" glyph) for a player who took damage.
 */
